import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { personHolidaysService } from '../services/personHolidays';
import { majorContractsService } from '@shared/services/majorContracts';

export default function PersonHolidays({ person, onClose }) {
    const { t } = useTranslation();
    const [paidLeaveSectionLocked, setPaidLeaveSectionLocked] = useState(true);
    const [mainContracts, setMainContracts] = useState([]);
    const [typeAOptions, setTypeAOptions] = useState([]);
    const [selectedTypeAId, setSelectedTypeAId] = useState('');
    const [selectedContractId, setSelectedContractId] = useState(1);
    const [holidays, setHolidays] = useState([]);
    const [typeALoading, setTypeALoading] = useState(false);
    const [holidaysLoading, setHolidaysLoading] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError] = useState('');
    const [saveLoading, setSaveLoading] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [remainingInput, setRemainingInput] = useState('');
    const [createYear, setCreateYear] = useState('');

    const defaultCreateYear = new Date().getFullYear() + 1;
    const createYearOptions = Array.from({ length: 21 }, (_, i) => defaultCreateYear - i);

    const handleUnlockPaidLeaveSection = () => {
        setPaidLeaveSectionLocked(false);
        if (!editingRecord) {
            setCreateYear(String(defaultCreateYear));
        }
    };

    useEffect(() => {
        const id = person?.recordId ?? person?.empId ?? person?.id;
        if (!id) return;

        majorContractsService
            .getMainContractsByPersonId(id)
            .then((data) => {
                const list = Array.isArray(data) ? data : (data?.content ?? []);
                setMainContracts(list);
                if (list.length && (selectedContractId === 1 || !selectedContractId)) {
                    setSelectedContractId(list[0].contractId);
                }
            })
            .catch((err) => console.error('Error fetching main contracts:', err));
    }, [person?.recordId, person?.empId, person?.id]);

 
    useEffect(() => {
        setTypeALoading(true);
        personHolidaysService
            .getTypeAOptions('PAYL')
            .then((data) => {
                const list = Array.isArray(data) ? data : (data?.content ?? []);
                setTypeAOptions(list);
            })
            .catch((err) => console.error('Error fetching type-a options:', err))
            .finally(() => setTypeALoading(false));
    }, []);

    const empId = person?.recordId

    const fetchHolidays = useCallback(() => {
        if (empId == null || selectedContractId == null) return;
        setHolidaysLoading(true);
        personHolidaysService
            .getRightFullHolidays(empId, selectedContractId)
            .then((data) => {
                const list = Array.isArray(data) ? data : (data?.content ?? []);
                setHolidays(list);
            })
            .catch((err) => console.error('Error fetching right-full-holidays:', err))
            .finally(() => setHolidaysLoading(false));
    }, [empId, selectedContractId]);

    useEffect(() => {
        fetchHolidays();
    }, [fetchHolidays]);

    const handleRemainingInputChange = (rawValue) => {
        if (paidLeaveSectionLocked) {
            return;
        }

        if (rawValue === '') {
            if (editingRecord) {
                setEditingRecord({ ...editingRecord, rightFullDays: '' });
            }
            setRemainingInput('');
            return;
        }

        const next = Number(rawValue);
        if (Number.isNaN(next) || next < 0) {
            return;
        }

        if (editingRecord) {
            setEditingRecord({ ...editingRecord, rightFullDays: next });
        }
        setRemainingInput(rawValue);
    };

    const handlePaidLeaveSave = () => {
        setEditError('');

        const remainingNumber = remainingInput === '' ? 0 : Number(remainingInput);
        if (Number.isNaN(remainingNumber) || remainingNumber < 0) {
            setEditError(t('persons.personHolidays.remainingDaysMin'));
            return;
        }

        setSaveLoading(true);

        const safeRemaining = remainingNumber;

        const toTypeAId = (val) => {
            const n = Number(val);
            return Number.isNaN(n) ? val : n;
        };

        if (editingRecord?.id) {
            const payload = {
                empId: editingRecord.empId,
                contractId: editingRecord.contractId,
                year: editingRecord.year,
                typeAId: toTypeAId(editingRecord.typeAId),
                rightFullDays: safeRemaining,
                usedDays: editingRecord.usedDays ?? 0,
                sumProv: editingRecord.sumProv,
                numbDays: editingRecord.numbDays,
                baseDays: editingRecord.baseDays
            };

            personHolidaysService
                .updateRightFullHoliday(editingRecord.id, payload)
                .then(() => {
                    handleCancelEdit();
                    fetchHolidays();
                })
                .catch((err) => {
                    const msg = err.response?.data?.message || err.message || t('common.error');
                    setEditError(msg);
                })
                .finally(() => setSaveLoading(false));
        } else {
            // POST: create new record with selected code/year and remaining value
            const payload = {
                empId,
                contractId: selectedContractId,
                year: createYear === '' ? null : Number(createYear),
                typeAId: toTypeAId(selectedTypeAId),
                rightFullDays: safeRemaining,
                usedDays: 0,
                sumProv: 0,
                numbDays: 0,
                baseDays: 20
            };

            personHolidaysService
                .createRightFullHoliday(payload)
                .then(() => {
                    handleCancelEdit();
                    fetchHolidays();
                })
                .catch((err) => {
                    const msg = err.response?.data?.message || err.message || t('common.error');
                    setEditError(msg);
                })
                .finally(() => setSaveLoading(false));
        }
    };

    const handleDeleteClick = (rowId) => {
        if (!window.confirm('Сигурни ли сте, че искате да изтриете записа?')) return;
        setDeleteError('');
        personHolidaysService
            .deleteRightFullHoliday(rowId)
            .then(() => {
                fetchHolidays();
                if (editingRecord?.id === rowId) handleCancelEdit();
            })
            .catch((err) => {
                const msg = err.response?.data?.message || err.message || t('common.error');
                setDeleteError(msg);
            });
    };

    const handleEditClick = (rowId) => {
        setEditError('');
        setDeleteError('');
        setEditLoading(true);
        personHolidaysService
            .getRightFullHolidayById(rowId)
            .then((data) => {
                if (data?.message && data?.id == null) {
                    setEditError(data.message);
                    return;
                }
                setEditingRecord(data);
                setPaidLeaveSectionLocked(false);
            })
            .catch((err) => {
                const msg = err.response?.data?.message || err.message || t('common.error');
                setEditError(msg);
            })
            .finally(() => setEditLoading(false));
    };

    const handleCancelEdit = () => {
        setPaidLeaveSectionLocked(true);
        setEditingRecord(null);
        setEditError('');
        setDeleteError('');
        setRemainingInput('');
        setSelectedTypeAId('');
        setCreateYear('');
    };

    const formRecord = editingRecord ?? {};

    useEffect(() => {
        if (editingRecord) {
            const initialRightFullDays = editingRecord.rightFullDays ?? 0;
            setRemainingInput(String(initialRightFullDays));
        } else {
            setRemainingInput('');
        }
    }, [editingRecord]);

    const typeAOptionsForSelect = (() => {
        const list = [...typeAOptions];
        if (editingRecord?.typeAId != null && editingRecord?.typeAName != null) {
            const hasCurrent = list.some((opt) => Number(opt.id) === Number(editingRecord.typeAId));
            if (!hasCurrent) {
                list.unshift({
                    id: editingRecord.typeAId,
                    nameWithCode: editingRecord.typeAName,
                    name: editingRecord.typeAName
                });
            }
        }
        return list;
    })();

    const typeASelectValue = editingRecord
        ? String(editingRecord.typeAId ?? '')
        : selectedTypeAId;

    return (
        <div className="main-person-form-container">
            <h3 className="form-subtitle">
                {t('persons.personHolidays.title', {
                    name: [person.name, person.fathersName, person.surname]
                        .filter(Boolean)
                        .join(' ')
                })}
            </h3>
            <div className="flex-column width-100 position-center gap-10 mt-10 form-border-radius">
                <div className="flex-column width-50 position-relative">
                    <label className="text-left label-absolute">{t('persons.personHolidays.employmentContracts')}</label>
                    <select
                        className="small-select"
                        value={selectedContractId ?? ''}
                        onChange={(e) => setSelectedContractId(Number(e.target.value))}
                        disabled={paidLeaveSectionLocked}
                    >
                        {mainContracts.map((c) => (
                            <option key={c.contractId} value={c.contractId}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex-column width-50 position-relative mt-20">
                    <label className="text-left label-absolute">{t('persons.personHolidays.changePaidLeave')}</label>
                    <span className="background-color-white-blue" >{t('persons.personHolidays.paidLeaveYear')}</span>
                    {editError && (
                        <div className="text-danger margin-top-10" role="alert">{editError}</div>
                    )}
                    {editLoading && (
                        <div className="margin-top-10">{t('common.loading')}</div>
                    )}
                    <div className="flex-row">
                        {/* 1 */}
                        <div className="flex-column width-50 gap-10">
                            <span>{t('persons.personHolidays.paidLeaveCode')}</span>
                            <span>{t('persons.personHolidays.forYear')}</span>
                            <span className="no-wrap">{t('persons.personHolidays.annualLeave')}</span>
                            <span>{t('persons.personHolidays.additionalLeave')}</span>
                            <span>{t('persons.personHolidays.dueDays')}</span>
                        </div>
                        {/* 2 */}
                        <div className="flex-column gap-10">
                            <select
                                className="small-select"
                                value={typeASelectValue}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (!editingRecord) {
                                        setSelectedTypeAId(val);
                                    }
                                }}
                                disabled={typeALoading || paidLeaveSectionLocked || Boolean(editingRecord)}
                            >
                                <option value="">{typeALoading ? t('common.loading') : '—'}</option>
                                {typeAOptionsForSelect.map((opt) => (
                                    <option key={opt.id} value={String(opt.id)}>
                                        {opt.nameWithCode ?? opt.name ?? opt.id}
                                    </option>
                                ))}
                            </select>
                            <select
                                className="small-select width-50"
                                value={editingRecord ? (formRecord.year ?? '') : (createYear ?? '')}
                                disabled={paidLeaveSectionLocked || Boolean(editingRecord)}
                                onChange={(e) => {
                                    if (editingRecord) return;
                                    setCreateYear(e.target.value);
                                }}
                            >
                                <option value="">—</option>
                                {editingRecord ? (
                                    formRecord.year != null && formRecord.year !== '' && (
                                        <option value={formRecord.year}>{formRecord.year}</option>
                                    )
                                ) : (
                                    createYearOptions.map((y) => (
                                        <option key={y} value={y}>{y}</option>
                                    ))
                                )}
                            </select>
                            <input
                                type="number"
                                className="small-input width-50"
                                value={20}
                                onChange={(e) => editingRecord && setEditingRecord({ ...editingRecord, rightFullDays: e.target.value === '' ? null : Number(e.target.value) })}
                                disabled={true}
                            />
                            <input
                                type="number"
                                className="small-input width-50"
                                value={0 ?? ''}
                                disabled={true}
                            />
                            <input
                                type="number"
                                className="small-input width-50"
                                min={0}
                                value={editingRecord ? (formRecord.rightFullDays ?? '') : (remainingInput ?? '')}
                                onChange={(e) => handleRemainingInputChange(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
                                        e.preventDefault();
                                    }
                                }}
                                readOnly={paidLeaveSectionLocked}
                            />
                        </div>
                        {/* 3 */}
                        {paidLeaveSectionLocked ? (
                            <div className="flex-column justify-flex-end margin-left-auto">
                                <button
                                    type="button"
                                    className="form-btn btn-blue height-100 width-100"
                                    onClick={handleUnlockPaidLeaveSection}
                                >
                                     {t('common.input')}
                                </button>
                            </div>
                        ) : (
                            <div className="flex-column justify-flex-end gap-10 margin-left-auto">
                                <button
                                    type="button"
                                    className="form-btn btn-blue height-80 width-100"
                                    onClick={handlePaidLeaveSave}
                                    disabled={saveLoading}
                                >
                                    {saveLoading ? t('common.loading') : t('common.save')}
                                </button>
                                <button
                                    type="button"
                                    className="form-btn btn-red height-80 width-100"
                                    onClick={handleCancelEdit}
                                >
                                    {t('common.cancel')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex-column width-50 mt-20">
                    <span className="background-color-white-blue">{t('persons.personHolidays.provisioned')}</span>
                    {/* 1 */}
                    <div className="flex-row">
                        <div className="flex-column width-50 gap-10">
                            <span>{t('persons.personHolidays.days')}</span>
                            <span>{t('persons.personHolidays.ammount')}</span>
                        </div>
                        {/* 2 */}
                        <div className="flex-column gap-10">
                            <input
                                type="number"
                                className="small-input width-50"
                                value={formRecord.numbDays ?? ''}
                                disabled={true}
                            />
                            <input
                                type="number"
                                className="small-input width-50"
                                value={formRecord.sumProv ?? ''}
                                disabled={true}
                            />
                        </div>
                    </div>
                </div>
                {deleteError && (
                    <div className="text-danger margin-top-10" role="alert">{deleteError}</div>
                )}
                <table className="table">
                    <thead>
                        <tr>
                            <th></th>
                            <th></th>
                            <th>{t('persons.personHolidays.forYear')}</th>
                            <th>{t('persons.personHolidays.code')}</th>
                            <th>{t('persons.personHolidays.basis')}</th>
                            <th>{t('persons.personHolidays.days')}</th>
                            <th>{t('persons.personHolidays.used')}</th>
                            <th>{t('persons.personHolidays.remaining')}</th>
                            <th>{t('persons.personHolidays.provisionedAmount')}</th>
                            <th>{t('persons.personHolidays.provisionedDays')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {holidaysLoading ? (
                            <tr>
                                <td colSpan={10} className="text-center">{t('common.loading')}</td>
                            </tr>
                        ) : holidays.length === 0 ? (
                            <tr>
                                <td colSpan={10} className="text-center">{t('common.dataNotFound')}</td>
                            </tr>
                        ) : (
                            holidays.map((row) => {
                                const remaining = (row.rightFullDays ?? 0) - (row.usedDays ?? 0);
                                return (
                                    <tr key={row.id}>
                                        <td>
                                            <img
                                                src='/images/icons/edit-mini.png'
                                                alt='edit'
                                                title={t('common.edit')}
                                                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                                onClick={(e) => { e.stopPropagation(); handleEditClick(row.id); }}
                                            />
                                        </td>
                                        <td>
                                            <img
                                                src='/images/icons/delete.png'
                                                alt='delete'
                                                title={t('common.remove')}
                                                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                                onClick={(e) => { e.stopPropagation(); handleDeleteClick(row.id); }}
                                            />
                                        </td>
                                        <td>{row.year ?? ''}</td>
                                        <td>{row.typeAId ?? ''}</td>
                                        <td>{row.typeAName ?? ''}</td>
                                        <td>{row.rightFullDays ?? ''}</td>
                                        <td>{row.usedDays ?? ''}</td>
                                        <td>{remaining}</td>
                                        <td>{row.sumProv ?? ''}</td>
                                        <td>{row.numbDays ?? ''}</td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
            <div className="form-controls form-border-radius">
                <button className="form-btn btn-red" onClick={onClose}>{t('common.exit')}</button>
            </div>
        </div >
    );
}
